import React, { useState, useEffect } from 'react';
import { useRouteError, useNavigate } from "react-router-dom";
import { 
    Container, 
    Typography, 
    Button, 
    Card, 
    CardContent,
    Box,
    Stack
} from "@mui/material";
import HomeIcon from '@mui/icons-material/Home';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useUser } from '../components/userContext';

export default function ErrorPage() {
    const error = useRouteError();
    const navigate = useNavigate();
    const { user } = useUser();
    const [logoSet, setLogoSet] = useState('coderis');

    console.error(error);

    // Fetch logo set configuration
    useEffect(() => {
        const fetchUserSettings = async () => {
            if (!user?.api_key) return;
            
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URI}/api/user-settings`, {
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.settings && data.settings.logoSet) {
                        setLogoSet(data.settings.logoSet);
                    }
                }
            } catch (error) {
                console.error('Error fetching user settings:', error);
            }
        };

        if (user) {
            fetchUserSettings();
        }
    }, [user]);

    // Determine which logo to use
    const getLogoImage = () => {
        if (logoSet === 'drk-bos') {
            return '/drk-logo.jpg';
        }
        return '/coderis_Logo.svg';
    };

    // Get error message based on error type
    const getErrorMessage = () => {
        if (error?.status === 404) {
            return {
                title: "Seite nicht gefunden",
                message: "Die angeforderte Seite existiert nicht oder wurde verschoben."
            };
        } else if (error?.status === 403) {
            return {
                title: "Zugriff verweigert",
                message: "Sie haben keine Berechtigung, diese Seite aufzurufen."
            };
        } else if (error?.status === 500) {
            return {
                title: "Serverfehler",
                message: "Ein interner Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut."
            };
        } else {
            return {
                title: "Ein Fehler ist aufgetreten",
                message: error?.statusText || error?.message || "Ein unerwarteter Fehler ist aufgetreten."
            };
        }
    };

    const handleBackToHome = () => {
        // Navigate to dashboard if user is logged in, otherwise to login
        if (user) {
            navigate('/dashboard');
        } else {
            navigate('/login');
        }
    };

    const errorInfo = getErrorMessage();

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Card elevation={3}>
                <CardContent>
                    <Stack spacing={3} alignItems="center">
                        {/* Logo */}
                        <Box sx={{ mb: 2 }}>
                            <img 
                                src={getLogoImage()} 
                                alt="Logo" 
                                style={{ 
                                    height: '80px',
                                    maxWidth: '200px',
                                    objectFit: 'contain'
                                }} 
                            />
                        </Box>

                        {/* Error Icon */}
                        <ErrorOutlineIcon 
                            sx={{ 
                                fontSize: 64, 
                                color: 'error.main',
                                opacity: 0.8
                            }} 
                        />

                        {/* Error Title */}
                        <Typography 
                            variant="h4" 
                            component="h1" 
                            gutterBottom
                            sx={{ fontWeight: 'bold', textAlign: 'center' }}
                        >
                            {errorInfo.title}
                        </Typography>

                        {/* Error Message */}
                        <Typography 
                            variant="body1" 
                            color="text.secondary"
                            sx={{ textAlign: 'center', maxWidth: '400px' }}
                        >
                            {errorInfo.message}
                        </Typography>

                        {/* Error Details (if available) */}
                        {error?.status && (
                            <Typography 
                                variant="caption" 
                                color="text.disabled"
                                sx={{ fontFamily: 'monospace' }}
                            >
                                Fehlercode: {error.status}
                            </Typography>
                        )}

                        {/* Back to Home Button */}
                        <Button
                            variant="contained"
                            startIcon={<HomeIcon />}
                            onClick={handleBackToHome}
                            size="large"
                            sx={{ mt: 2 }}
                        >
                            Zurück zur Startseite
                        </Button>
                    </Stack>
                </CardContent>
            </Card>
        </Container>
    );
}